import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DateTime } from '@mode-switch/express';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { GoogleSheetUploadFacade } from '@mode/dataset/google/contract';
import { ConfirmDialogService } from '@mode/shared/ui';
import { GoogleAccountFacade, WorkspaceSettingsDomainModule } from '@mode/workspace-settings/domain';
import { MetaTags, MetaTagsService, LocalStorageService, HelpDocsService, getAppName } from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';

@Component({
  selector: 'mode-google-account',
  standalone: true,
  imports: [ButtonComponent, CommonModule, IconComponent, WorkspaceSettingsDomainModule],
  templateUrl: './google-account.component.html',
  styleUrls: ['./google-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleAccountComponent {
  @Input({ required: true }) disconnectUri!: string;
  @Input() email?: string;
  @Input() connectedAt?: string;
  @Input() lastUploadedAt?: string;
  readonly appName = getAppName();
  readonly helpLink = this.helpDocs.links['https://mode.com/help/articles/google-sheets-integration'];

  constructor(
    private _confirmDialogService: ConfirmDialogService,
    private _googleAccountFacade: GoogleAccountFacade,
    private _uploadSheetFacade: GoogleSheetUploadFacade,
    private analyticsService: AnalyticsService,
    private meta: MetaTagsService,
    private localStorageService: LocalStorageService,
    private helpDocs: HelpDocsService
  ) {}

  upload() {
    this.sendGSheetUploadLaunchEvent();
    this._uploadSheetFacade.launchDialog();
  }

  sendGSheetUploadLaunchEvent() {
    const eventData = {
      location: AnalyticsEvent.SubContext.GoogleSheetUploadAuthFlow,
      action: AnalyticsEvent.Name.GoogleSheetUploadLaunched,
      session_ID: this.localStorageService.getItem('LAST_ACTIVE_TAB_ID'),
      user_ID: this.meta.getTag(MetaTags.CurrentUsername),
      timestamp: DateTime,
    };
    this.analyticsService.track(AnalyticsEvent.Name.GoogleSheetUploadLaunched, eventData);
  }
  disconnect() {
    this._confirmDialogService.open({
      dialogTitle: 'Disconnect Google Account?',
      dialogText: `
        Disconnecting your Google Account will prevent you from creating new Datasets using Google Sheet data until you reconnect.<br /><br />

        <strong>Existing Datasets built on this connection will continue to use the last successful sync but won't be able to sync in new data unless you reconnect</strong>, which could break existing schedules.
      `,
      confirmText: 'Disconnect',
      confirmAction: () => this._googleAccountFacade.disconnect(this.disconnectUri),
    });
  }
}
